import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { userApi } from '../../api/user';
import { applicationApi } from '../../api/application';
import { UserCreateRequest, UserRole, UserType, SubsclightTenantUser } from '../../types/user';
import { validatePassword, getPasswordRequirementsText } from '../../utils/passwordValidation';

const roleLabels: Record<UserRole, string> = {
  ADMIN: '管理者',
  USER: '一般ユーザー',
};

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creationMethod, setCreationMethod] = useState<'subsclight' | 'manual'>('subsclight');
  const [tenantUsers, setTenantUsers] = useState<SubsclightTenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [manualEmail, setManualEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role] = useState<UserRole>('USER'); // 一般ユーザー固定
  const [error, setError] = useState<string | null>(null);

  // サブスクライトテナントユーザーを取得（サブスクライト選択時のみ）
  useEffect(() => {
    if (creationMethod === 'subsclight') {
      loadTenantUsers();
    }
  }, [creationMethod]);

  const loadTenantUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const response = await applicationApi.getTenantUsers();
      if (response.success) {
        setTenantUsers(response.users);
      } else {
        setError(response.error || 'テナントユーザーの取得に失敗しました');
      }
    } catch (err: any) {
      console.error('Failed to load tenant users:', err);
      setError(err.response?.data?.error || 'テナントユーザーの取得に失敗しました');
    } finally {
      setLoadingUsers(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: userApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
  });

  const onSubmit = async () => {
    try {
      // パスワードバリデーション
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        setError(passwordValidation.errors.join('\n'));
        return;
      }

      let createRequest: UserCreateRequest;

      if (creationMethod === 'subsclight') {
        // サブスクライト連携ユーザー作成
        if (!selectedUserId) {
          setError('ユーザーを選択してください');
          return;
        }

        const selectedUser = tenantUsers.find(user => user.id === selectedUserId);
        if (!selectedUser) {
          setError('選択されたユーザーが見つかりません');
          return;
        }

        createRequest = {
          email: selectedUser.email,
          password: password,
          role: role,
          userType: 'SUBSCLIGHT_LINKED' as UserType,
          tenantId: '', // バックエンドで自動設定
          subsclight_customer_id: selectedUser.id,
        };
      } else {
        // 独立ユーザー作成（手動入力）
        if (!manualEmail) {
          setError('メールアドレスを入力してください');
          return;
        }

        // メール形式のバリデーション
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(manualEmail)) {
          setError('正しいメールアドレス形式を入力してください');
          return;
        }

        createRequest = {
          email: manualEmail,
          password: password,
          role: role,
          userType: 'INDEPENDENT' as UserType,
          tenantId: '', // バックエンドで自動設定
        };
      }

      await createMutation.mutateAsync(createRequest);
    } catch (error) {
      console.error('ユーザーの作成に失敗しました:', error);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4">ユーザー新規作成</Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box component="form">
          <Grid container spacing={3}>
            {/* 作成方法の選択 */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">ユーザー作成方法</FormLabel>
                <RadioGroup
                  row
                  value={creationMethod}
                  onChange={(e) => {
                    setCreationMethod(e.target.value as 'subsclight' | 'manual');
                    setError(null);
                    setSelectedUserId('');
                    setManualEmail('');
                  }}
                >
                  <FormControlLabel
                    value="subsclight"
                    control={<Radio />}
                    label="サブスクライトユーザーから選択"
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio />}
                    label="任意のメールアドレスで作成"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

        {creationMethod === 'subsclight' && loadingUsers ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={5}>
            <CircularProgress size={32} sx={{ mr: 2 }} />
            テナントユーザーを取得中...
          </Box>
        ) : creationMethod === 'subsclight' ? (
              <>
              {/* サブスクライトユーザー選択 */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>サブスクライトユーザー</InputLabel>
                  <Select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value as number)}
                    label="サブスクライトユーザー"
                    disabled={tenantUsers.length === 0}
                  >
                    {tenantUsers.map((user) => (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                        disabled={user.is_duplicate}
                      >
                        {user.display_name || user.name} ({user.email})
                        {user.is_duplicate && ' (既に登録済み)'}
                      </MenuItem>
                    ))}
                  </Select>
                  {tenantUsers.length === 0 && (
                    <Box mt={1} color="text.secondary" fontSize="0.875rem">
                      サブスクライトAPIの設定を確認してください
                    </Box>
                  )}
                </FormControl>
              </Grid>
              </>
        ) : (
          // 手動メールアドレス入力
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="メールアドレス"
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                helperText="ユーザーのメールアドレスを入力してください"
              />
            </Grid>
          </>
        )}

            {/* 共通フィールド */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="パスワード"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={creationMethod === 'subsclight' && tenantUsers.length === 0}
                helperText={getPasswordRequirementsText()}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ロール"
                value="一般ユーザー"
                disabled
                helperText="新規作成ユーザーは一般ユーザーに固定されます"
              />
            </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/users')}
                    disabled={createMutation.isPending}
                  >
                    キャンセル
                  </Button>
                  <Button
                    onClick={onSubmit}
                    variant="contained"
                    color="primary"
                    disabled={createMutation.isPending || (creationMethod === 'subsclight' && (loadingUsers || tenantUsers.length === 0))}
                  >
                    {createMutation.isPending ? (
                      <CircularProgress size={24} />
                    ) : (
                      '作成'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
      </Paper>

      {(createMutation.isError || error) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'ユーザーの作成に失敗しました。もう一度お試しください。'}
        </Alert>
      )}
    </Box>
  );
};

export default UserCreatePage; 